import { AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS } from '@ipfabric/utils/aim/flow-protocols';
import { splitCellList } from 'components/DataTransferForm/helpers/csvLineParser';
import type { CSVParseConfig } from 'components/DataTransferForm/types';
import { generateGuid } from 'utils/guid';
import { z } from 'zod';
import type {
  AimApplicationDraft,
  AimFlowDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';

// Max import file size: 10MB, matches bgpCsvConfig MAX_CSV_FILE_SIZE.
export const MAX_CSV_FILE_SIZE = 10 * 1024 * 1024;

// UUID format check — 8-4-4-4-12 hex groups. Deliberately not enforcing RFC 4122
// version/variant bits so user-supplied IDs (e.g. all-2s test UUIDs) are accepted.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const emptyToNull = (s: string): string | null => (s.trim() === '' ? null : s);

const ipSchema = z.string().ip();

const validateInterfaceIPs = (interfaceIps: string[]): void => {
  for (const ip of interfaceIps) {
    if (!ipSchema.safeParse(ip).success) {
      throw new Error(`Interface "${ip}" is not a valid IP address`);
    }
  }
};

type FactoryArgs = {
  isReplaceMode: boolean;
  existingIds: Set<string>;
};

const APP_HEADERS = ['id', 'name', 'environment'] as const;

export const buildAimApplicationsCsvConfig = (
  args: FactoryArgs,
): CSVParseConfig<AimApplicationDraft> => {
  const seenInFile = new Set<string>();
  return {
    headers: APP_HEADERS,
    maxFileSizeBytes: MAX_CSV_FILE_SIZE,
    rules: [
      { field: 'id', required: false },
      {
        field: 'name',
        required: true,
        validator: (v) => (v.trim() ? null : 'name is required'),
      },
      { field: 'environment', required: false },
    ],
    rowTransformer: (row) => {
      const idRaw = ((row.id as string) ?? '').trim();
      if (idRaw && !UUID_RE.test(idRaw)) {
        throw new Error(`Invalid id "${idRaw}"`);
      }
      const id = idRaw || generateGuid();
      if (seenInFile.has(id)) {
        throw new Error(`Duplicate id "${id}" within this file`);
      }
      if (!args.isReplaceMode && args.existingIds.has(id)) {
        throw new Error(`Duplicate id "${id}" — already in table`);
      }
      seenInFile.add(id);
      return {
        id,
        name: row.name as string,
        environment: emptyToNull(row.environment as string),
      };
    },
  };
};

const WORKLOAD_HEADERS = [
  'id',
  'applicationId',
  'name',
  'ipAddress',
  'interfaces (list of IP addresses)',
] as const;

export const buildAimWorkloadsCsvConfig = (
  args: FactoryArgs,
): CSVParseConfig<AimWorkloadDraft> => {
  const seenInFile = new Set<string>();
  return {
    headers: WORKLOAD_HEADERS,
    maxFileSizeBytes: MAX_CSV_FILE_SIZE,
    rules: [
      { field: 'id', required: false },
      {
        field: 'applicationId',
        required: true,
        validator: (v) => (v.trim() ? null : 'applicationId is required'),
      },
      {
        field: 'name',
        required: true,
        validator: (v) => (v.trim() ? null : 'name is required'),
      },
      { field: 'ipAddress', required: false },
      {
        field: 'interfaces (list of IP addresses)',
        required: false,
        validator: (value) =>
          value.trim() ? null : 'At least one interface IP is required',
        transformer: (value, _row, csvDelimiter) =>
          splitCellList(value, csvDelimiter),
      },
    ],
    rowTransformer: (row) => {
      const idRaw = ((row.id as string) ?? '').trim();
      if (idRaw && !UUID_RE.test(idRaw)) {
        throw new Error(`Invalid id "${idRaw}"`);
      }
      const id = idRaw || generateGuid();
      if (seenInFile.has(id)) {
        throw new Error(`Duplicate id "${id}" within this file`);
      }
      if (!args.isReplaceMode && args.existingIds.has(id)) {
        throw new Error(`Duplicate id "${id}" — already in table`);
      }
      seenInFile.add(id);

      const interfaceIps = Array.isArray(
        row['interfaces (list of IP addresses)'],
      )
        ? (row['interfaces (list of IP addresses)'] as string[])
        : [];

      validateInterfaceIPs(interfaceIps);

      return {
        id,
        applicationId: (row.applicationId as string).trim(),
        name: row.name as string,
        ipAddress: (row.ipAddress as string)?.trim() || null,
        interfaces: interfaceIps,
      };
    },
  };
};

const FLOW_HEADERS = [
  'id',
  'srcWorkloadId',
  'dstWorkloadId',
  'srcIp',
  'dstIp',
  'protocol',
  'srcPort',
  'dstPort',
] as const;

const portValidator =
  (fieldName: string) =>
  (v: string): string | null => {
    if (v.trim() === '') {
      return null;
    }

    const num = Number(v);
    return Number.isInteger(num) && num >= 0 && num <= 65535
      ? null
      : `${fieldName} must be a number between 0 and 65535`;
  };

const parsePort = (v: string): number | null =>
  v.trim() === '' ? null : Number(v);

const protocolValidator = (v: string): string | null => {
  if (v.trim() === '') {
    return 'protocol is required';
  }

  return AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS[
    v as keyof typeof AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS
  ]
    ? null
    : `protocol must be one of the allowed numeric values: ${Object.entries(
        AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS,
      )
        .map(([value, label]) => `${value}: ${label}`)
        .join(', ')}`;
};

export const buildAimFlowsCsvConfig = (
  args: FactoryArgs,
): CSVParseConfig<AimFlowDraft> => {
  const seenInFile = new Set<string>();
  return {
    headers: FLOW_HEADERS,
    maxFileSizeBytes: MAX_CSV_FILE_SIZE,
    rules: [
      { field: 'id', required: false },
      {
        field: 'srcWorkloadId',
        required: true,
        validator: (v) => (v.trim() ? null : 'srcWorkloadId is required'),
      },
      {
        field: 'dstWorkloadId',
        required: true,
        validator: (v) => (v.trim() ? null : 'dstWorkloadId is required'),
      },
      {
        field: 'srcIp',
        required: true,
        validator: (v) => (v.trim() ? null : 'srcIp is required'),
      },
      {
        field: 'dstIp',
        required: true,
        validator: (v) => (v.trim() ? null : 'dstIp is required'),
      },
      {
        field: 'protocol',
        required: true,
        validator: protocolValidator,
      },
      {
        field: 'srcPort',
        required: false,
        validator: portValidator('srcPort'),
      },
      {
        field: 'dstPort',
        required: false,
        validator: portValidator('dstPort'),
      },
    ],
    rowTransformer: (row) => {
      const idRaw = ((row.id as string) ?? '').trim();
      if (idRaw && !UUID_RE.test(idRaw)) {
        throw new Error(`Invalid id "${idRaw}"`);
      }
      const id = idRaw || generateGuid();
      if (seenInFile.has(id)) {
        throw new Error(`Duplicate id "${id}" within this file`);
      }
      if (!args.isReplaceMode && args.existingIds.has(id)) {
        throw new Error(`Duplicate id "${id}" — already in table`);
      }
      seenInFile.add(id);

      return {
        id,
        srcWorkloadId: (row.srcWorkloadId as string).trim(),
        dstWorkloadId: (row.dstWorkloadId as string).trim(),
        srcIp: (row.srcIp as string).trim(),
        dstIp: (row.dstIp as string).trim(),
        protocol: Number(row.protocol),
        srcPort: parsePort((row.srcPort as string) ?? ''),
        dstPort: parsePort((row.dstPort as string) ?? ''),
      };
    },
  };
};
